import { Controller } from '@hotwired/stimulus'
import type AsyncRenderErrorController from './async_render_error_controller'
import type { SolidRequestContext } from '@app/shared/types/sf-data'
import { mountSolidTemplate } from '@app/adapters/solid/mount-solid-template'

export default class extends Controller {
  static targets = ['skeleton', 'mount', 'error']
  static values = { entry: String, stylesheets: Array, context: Object }
  static outlets = ['async-render-error']

  declare readonly skeletonTarget: HTMLElement
  declare readonly mountTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly entryValue: string
  declare readonly stylesheetsValue: string[]
  declare readonly contextValue: SolidRequestContext
  declare readonly asyncRenderErrorOutlet: AsyncRenderErrorController
  declare readonly hasAsyncRenderErrorOutlet: boolean

  // Stimulus reuses the controller instance when its element is detached and
  // re-attached, so each connect aborts only its own mount.
  private abortController: AbortController | null = null

  connect(): void {
    const abortController = new AbortController()

    this.abortController = abortController
    this.reset()
    void this.mountTemplate(abortController.signal)
  }

  disconnect(): void {
    this.abortController?.abort()
    this.abortController = null
  }

  private async mountTemplate(signal: AbortSignal): Promise<void> {
    const result = await mountSolidTemplate({
      boot: {
        entryUrl: this.entryValue,
        stylesheetUrls: this.stylesheetsValue,
        requestContext: this.contextValue,
      },
      el: this.mountTarget,
      signal,
    })

    if (result === 'mounted') {
      this.show(this.mountTarget)
    }

    if (result === 'failed') {
      this.show(this.errorTarget)

      // The outlet getter throws when the markup does not wire the outlet.
      if (this.hasAsyncRenderErrorOutlet) {
        this.asyncRenderErrorOutlet.show()
      }
    }
  }

  // A Turbolinks cache restore replays the last rendered HTML, the revealed
  // template included, before connect() fires.
  private reset(): void {
    this.mountTarget.replaceChildren()
    this.errorTarget.replaceChildren()
    this.show(this.skeletonTarget)
  }

  private show(visible: HTMLElement): void {
    ;[this.skeletonTarget, this.mountTarget, this.errorTarget].forEach(
      (target) => {
        target.classList.toggle('tw-hidden', target !== visible)
      }
    )
  }
}
